import { FC, useState, useEffect } from 'react';
import {
    Grid,
    TextField,
    Button,
    Paper,
    Typography,
    Box,
    CircularProgress,
    Alert,
    Checkbox,
    FormControlLabel,
    Switch,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { SCUIContent } from '../../../atomic/organisms';
import { SCUICustomDataGrid } from '../../../atomic/molecules';
import { SCUIButton } from '../../../atomic/atoms';
import GlobalDataQuery from '../../../services/query/globaldata/GlobalDataQuery.services';
import GlobaldataCommand from '../../../services/command/globaldata/GlobaldataCommand.services';
import Swal from 'sweetalert2';
import DeleteIcon from '@mui/icons-material/Delete';
import { GDCM } from '../globalData.classes';

interface RedisKey {
    key: string;
    value: any;
    type: string;
    ttl: number;
    size: number;
}

export const GlobalDataCacheManagement: FC = () => {
    const { t } = useTranslation();
    const [databaseNumber, setDatabaseNumber] = useState<string>('');
    const [keySearch, setKeySearch] = useState<string>('');
    const [redisKeys, setRedisKeys] = useState<RedisKey[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [selectedKeys, setSelectedKeys] = useState<string[]>([]);

    useEffect(() => {
        console.log('Redis Keys State Updated:', redisKeys);
    }, [redisKeys]);

    // Fetch default database number when component loads
    useEffect(() => {
        const fetchDefaultDatabase = async () => {
            try {
                const response = await GlobalDataQuery.GetGlobaldataDefaultDatabase();
                setDatabaseNumber(response.toString());
            } catch (error) {
                console.error('Error fetching default database:', error);
            }
        };

        fetchDefaultDatabase();
    }, []);

    const handleFilter = async () => {
        if (!databaseNumber.trim()) {
            Swal.fire(t('general.emptyFieldControlText'), '', 'error');
            return;
        }

        const dbNumber = parseInt(databaseNumber);
        if (isNaN(dbNumber) || dbNumber < 0) {
            Swal.fire(t('general.validFieldControlText'), '', 'error');
            return;
        }

        setLoading(true);
        setError('');
        setSelectedKeys([]); // Clear selections when new data is loaded

        try {
            // Create pattern based on search text
            let pattern = '*';
            if (keySearch.trim()) {
                pattern = `*${keySearch.trim()}*`;
            }

            const params = {
                databaseNumber: dbNumber,
                pattern: pattern,
                limit: 200000,
            };

            const response = await GlobalDataQuery.GetGlobaldataRedisData(params);
            console.log('API Response:', response);
            // Check if response has the expected structure
            if (response && response.data.databases && response.data.databases.length > 0) {
                setRedisKeys(response.data.databases[0].keys || []);
            } else {
                setRedisKeys([]);
            }
        } catch (error) {
            console.error('Error fetching Redis data:', error);
            setError(t('globalDataCacheManagement.loading'));
            setRedisKeys([]);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteKey = async (key: string) => {
        const dbNumber = parseInt(databaseNumber);

        Swal.fire({
            title: t('globalDataCacheManagement.deleteKey'),
            text: t('globalDataCacheManagement.deleteConfirmation'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: t('general.delete'),
            cancelButtonText: t('general.cancel'),
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await GlobaldataCommand.GlobaldataDeleteRedisKey(dbNumber, key);
                    Swal.fire(t('globalDataCacheManagement.deleteSuccess'), '', 'success');
                    // Refresh the data after successful deletion
                    handleFilter();
                } catch (error) {
                    console.error('Error deleting Redis key:', error);
                    Swal.fire(t('globalDataCacheManagement.deleteError'), '', 'error');
                }
            }
        });
    };

    const handleBulkDelete = async () => {
        if (selectedKeys.length === 0) {
            Swal.fire(t('globalDataCacheManagement.noKeysSelected'), '', 'warning');
            return;
        }

        const dbNumber = parseInt(databaseNumber);

        Swal.fire({
            title: t('globalDataCacheManagement.bulkDeleteTitle'),
            text: t('globalDataCacheManagement.bulkDeleteConfirmation', { count: selectedKeys.length }),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: t('general.delete'),
            cancelButtonText: t('general.cancel'),
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    setLoading(true);
                    await GlobaldataCommand.GlobaldataBulkDeleteRedisKeys(dbNumber, selectedKeys);
                    Swal.fire(t('globalDataCacheManagement.bulkDeleteSuccess'), '', 'success');
                    setSelectedKeys([]);
                    // Refresh the data after successful deletion
                    handleFilter();
                } catch (error) {
                    console.error('Error bulk deleting Redis keys:', error);
                    Swal.fire(t('globalDataCacheManagement.bulkDeleteError'), '', 'error');
                } finally {
                    setLoading(false);
                }
            }
        });
    };

    const handleSelectAll = () => {
        if (selectedKeys.length === redisKeys.length) {
            // If all are selected, deselect all
            setSelectedKeys([]);
        } else {
            // Select all
            setSelectedKeys(redisKeys.map((key) => key.key));
        }
    };

    const handleSelectKey = (key: string, checked: boolean) => {
        if (checked) {
            setSelectedKeys((prev) => [...prev, key]);
        } else {
            setSelectedKeys((prev) => prev.filter((k) => k !== key));
        }
    };

    const handleClearFilters = () => {
        setKeySearch('');
        setDatabaseNumber('');
        setRedisKeys([]);
        setError('');
        setSelectedKeys([]);
    };

    const columns = [
        {
            field: 'select',
            headerName: '',
            width: 50,
            sortable: false,
            renderCell: (params: any) => (
                <Checkbox
                    className={GDCM.inputCheckboxRowSelect}
                    checked={selectedKeys.includes(params.row.key)}
                    onChange={(e) => handleSelectKey(params.row.key, e.target.checked)}
                    size="small"
                />
            ),
        },
        {
            field: 'key',
            headerName: t('globalDataCacheManagement.key'),
            flex: 1,
            minWidth: 300,
            renderCell: (params: any) => <div style={{ wordBreak: 'break-all' }}>{params.value}</div>,
        },
        {
            field: 'type',
            headerName: t('globalDataCacheManagement.type'),
            flex: 0.5,
            minWidth: 100,
        },
        {
            field: 'ttl',
            headerName: t('globalDataCacheManagement.ttl'),
            flex: 0.5,
            minWidth: 100,
            renderCell: (params: any) => <div>{params.value > 0 ? params.value : 'No TTL'}</div>,
        },
        {
            field: 'actions',
            headerName: t('general.actions'),
            flex: 0.5,
            minWidth: 100,
            renderCell: (params: any) => (
                <Button
                    className={GDCM.inputButtonDeleteKey}
                    sx={{ minWidth: '30px !important', height: '30px', borderRadius: '100%' }}
                    color="error"
                    size="small"
                    startIcon={<DeleteIcon />}
                    onClick={() => handleDeleteKey(params.row.key)}
                ></Button>
            ),
        },
    ];

    return (
        <SCUIContent>
            <Box className={GDCM.page}>
                <Grid className={GDCM.mainGrid} container spacing={3}>
                    <Grid
                        size={{
                            xs: 12,
                        }}
                    >
                        <Box className={GDCM.headerSection}>
                            <Typography variant="h4" component="h1" gutterBottom>
                                {t('globalDataCacheManagement.title')}
                            </Typography>
                            <Typography variant="body1" color="textSecondary" gutterBottom>
                                {t('globalDataCacheManagement.description')}
                            </Typography>
                        </Box>
                    </Grid>

                    <Grid
                        size={{
                            xs: 12,
                        }}
                    >
                        <Paper className={GDCM.filtersPaper} sx={{ p: 3 }}>
                            <Grid className={GDCM.filtersGrid} container spacing={2} alignItems="center">
                                <Grid
                                    size={{
                                        xs: 12,
                                        sm: 6,
                                        md: 3,
                                    }}
                                >
                                    <TextField
                                        className={GDCM.inputTextDatabaseNumber}
                                        fullWidth
                                        label={t('globalDataCacheManagement.databaseNumber')}
                                        placeholder={t('globalDataCacheManagement.enterDatabaseNumber')}
                                        type="number"
                                        value={databaseNumber}
                                        onChange={(e) => setDatabaseNumber(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                handleFilter();
                                            }
                                        }}
                                    />
                                </Grid>
                                <Grid
                                    size={{
                                        xs: 12,
                                        sm: 6,
                                        md: 4,
                                    }}
                                >
                                    <TextField
                                        className={GDCM.inputTextKeySearch}
                                        fullWidth
                                        label={t('globalDataCacheManagement.keySearch')}
                                        placeholder={t('globalDataCacheManagement.enterKeySearch')}
                                        type="text"
                                        value={keySearch}
                                        onChange={(e) => setKeySearch(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                handleFilter();
                                            }
                                        }}
                                    />
                                </Grid>
                                <Grid>
                                    <SCUIButton
                                        className={GDCM.inputButtonFilter}
                                        text={t('globalDataCacheManagement.filter')}
                                        type="primary"
                                        style={{ height: '36px' }}
                                        sizeType="fixed"
                                        size="m"
                                        onClick={handleFilter}
                                        disabled={loading}
                                    />
                                </Grid>
                                <Grid>
                                    <SCUIButton
                                        className={GDCM.inputButtonClear}
                                        text={t('general.clear')}
                                        type="secondary"
                                        style={{ height: '36px' }}
                                        sizeType="fixed"
                                        size="m"
                                        onClick={handleClearFilters}
                                        disabled={loading}
                                    />
                                </Grid>
                            </Grid>
                        </Paper>
                </Grid>

                {loading && (
                    <Grid
                        size={{
                            xs: 12,
                        }}
                    >
                        <Box
                            className={GDCM.loadingSection}
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            p={3}
                        >
                            <CircularProgress />
                            <Typography variant="body1" sx={{ ml: 2 }}>
                                {t('globalDataCacheManagement.loading')}
                            </Typography>
                        </Box>
                    </Grid>
                )}

                {error && (
                    <Grid
                        size={{
                            xs: 12,
                        }}
                    >
                        <Alert severity="error">{error}</Alert>
                    </Grid>
                )}

                {!loading && !error && (
                    <Grid
                        size={{
                            xs: 12,
                        }}
                    >
                        <Paper className={GDCM.resultsPaper} sx={{ p: 3 }}>
                            <Box
                                className={GDCM.resultsToolbar}
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                                mb={2}
                            >
                                <Typography variant="h6" gutterBottom>
                                    {t('globalDataCacheManagement.redisKeys')} ({redisKeys.length})
                                </Typography>
                                {redisKeys.length > 0 && (
                                    <Box display="flex" gap={2}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    className={GDCM.inputSwitchSelectAll}
                                                    checked={
                                                        selectedKeys.length === redisKeys.length && redisKeys.length > 0
                                                    }
                                                    onChange={handleSelectAll}
                                                />
                                            }
                                            label={t('globalDataCacheManagement.selectAll')}
                                        />
                                        {selectedKeys.length > 0 && (
                                            <SCUIButton
                                                className={GDCM.inputButtonBulkDelete}
                                                text={`${t('globalDataCacheManagement.bulkDelete')} (${
                                                    selectedKeys.length
                                                })`}
                                                type="error"
                                                sizeType="fixed"
                                                size="m"
                                                onClick={handleBulkDelete}
                                                disabled={loading}
                                            />
                                        )}
                                    </Box>
                                )}
                            </Box>

                            {redisKeys.length > 0 ? (
                                <div className={GDCM.gridWrap} style={{ height: 600, width: '100%' }}>
                                    <SCUICustomDataGrid
                                        rows={redisKeys}
                                        columns={columns}
                                        getRowId={(row: RedisKey) => row.key}
                                        disableRowSelectionOnClick
                                    />
                                </div>
                            ) : (
                                <Box
                                    className={GDCM.emptyStateWrap}
                                    display="flex"
                                    justifyContent="center"
                                    alignItems="center"
                                    p={3}
                                >
                                    <Typography variant="body1" color="textSecondary">
                                        {t('globalDataCacheManagement.noDataFound')} (Keys count: {redisKeys.length})
                                    </Typography>
                                </Box>
                            )}
                        </Paper>
                    </Grid>
                )}
                </Grid>
            </Box>
        </SCUIContent>
    );
};
